import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { videosApi, FINISHING_LABEL, type VideoWithFinishing } from "@/api/videos.ts";
import {
  PageContainer, TfCard, EmptyState, Btn, Icon, StatusPill,
  FormatBadge, VideoThumb, ToggleGroup, Checkbox, TfSpinner, STATUS_CONFIG,
} from "@/components/v2/primitives.tsx";
import { materialBadge, withMaterial } from "@/lib/material.ts";   // (B112)
import type { Video } from "@vms/tubeforge-shared";

const PAGE_SIZE = 50;

/** Session 6 W2: a rendered video's status in the finishing run's words — "Rendered — awaiting finishing", "Finishing",
 *  "Awaiting approval", "A newer finishing run is in progress", "Ready to post" — with the reason as the tooltip.
 *  Everything else keeps its status word. */
function VideoStatus({ v }: { v: Video }) {
  const f = (v as VideoWithFinishing).finishing;
  // (B112; planning 20:50) a video held at its final review: "Ready · 3 changes" (0: "· as planned"), from the callback's
  //  count; none known (NULL): the word alone, as before
  const label = withMaterial(f ? FINISHING_LABEL[f.state] : STATUS_CONFIG[v.status]?.label, materialBadge((v as VideoWithFinishing).materialCount));
  return <span title={f?.reason ?? undefined}><StatusPill status={v.status} size="sm" label={label} /></span>;
}

function videoStageRoute(v: Video): string {
  const s = v.status;
  if (s === "script_generating" || s === "script_ready") return `/videos/${v.id}/script`;
  if (s === "script_approved" || s === "storyboard_generating" || s === "storyboard_ready") return `/videos/${v.id}/storyboard`;
  if (["storyboard_approved","generating_clips","voiceover_generating","rendering"].includes(s)) return `/videos/${v.id}/render`;
  return `/videos/${v.id}`;
}

type FilterId = "all" | "drafts" | "rendering" | "ready" | "published" | "failed";

const DRAFT_STATUSES = ["draft","script_generating","script_ready","script_approved","storyboard_generating","storyboard_ready","storyboard_approved"];
const RENDERING_STATUSES = ["generating_clips","voiceover_generating","rendering"];

function matchesFilter(v: Video, filter: FilterId): boolean {
  if (filter === "all") return true;
  if (filter === "drafts") return DRAFT_STATUSES.includes(v.status);
  if (filter === "rendering") return RENDERING_STATUSES.includes(v.status);
  if (filter === "ready") return v.status === "complete";
  if (filter === "published") return v.status === "published";
  if (filter === "failed") return v.status === "failed" || v.status === "publish_failed";
  return true;
}

export function VideosListPage() {
  const navigate = useNavigate();
  const [view, setView] = useState("list");
  const [filter, setFilter] = useState<FilterId>("all");
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const { data, isLoading } = useQuery({
    queryKey: ["videos", { limit: PAGE_SIZE }],
    queryFn: () => videosApi.list({ limit: PAGE_SIZE }),
  });

  const allVideos = data?.videos ?? [];
  const visible = allVideos.filter(v => matchesFilter(v, filter));

  const filters: Array<{ id: FilterId; label: string; count: number }> = [
    { id: "all",       label: "All",       count: allVideos.length },
    { id: "drafts",    label: "Drafts",    count: allVideos.filter(v => DRAFT_STATUSES.includes(v.status)).length },
    { id: "rendering", label: "Rendering", count: allVideos.filter(v => RENDERING_STATUSES.includes(v.status)).length },
    { id: "ready",     label: "Ready",     count: allVideos.filter(v => v.status === "complete").length },
    { id: "published", label: "Published", count: allVideos.filter(v => v.status === "published").length },
    { id: "failed",    label: "Failed",    count: allVideos.filter(v => v.status === "failed" || v.status === "publish_failed").length },
  ];

  const toggleSelect = (id: string) => {
    setSelected(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  };
  const toggleAll = () => {
    if (selected.size === visible.length) setSelected(new Set());
    else setSelected(new Set(visible.map(v => v.id)));
  };

  return (
    <PageContainer title="Videos" subtitle={`${allVideos.length} videos`}
      action={<Btn icon="plus" size="lg" onClick={() => navigate("/videos/new")}>New video</Btn>} maxWidth={1400}>

      {/* Filter tabs + view toggle */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 16, marginBottom: 16, flexWrap: "wrap" }}>
        <div style={{ display: "flex", gap: 4, overflowX: "auto" }}>
          {filters.map(f => {
            const sel = filter === f.id;
            return (
              <button key={f.id} onClick={() => { setFilter(f.id); setSelected(new Set()); }} style={{
                padding: "8px 14px", borderRadius: 8,
                background: sel ? "var(--tf-bg)" : "transparent",
                border: "1px solid " + (sel ? "var(--tf-rule)" : "transparent"),
                color: sel ? "var(--tf-fg)" : "var(--tf-fg-muted)",
                fontFamily: "inherit", fontSize: 13, fontWeight: sel ? 600 : 500,
                cursor: "pointer", whiteSpace: "nowrap",
                boxShadow: sel ? "var(--tf-shadow-sm)" : "none",
              }}>
                {f.label} <span className="tf-mono tf-muted" style={{ marginLeft: 4, fontSize: 11 }}>{f.count}</span>
              </button>
            );
          })}
        </div>
        <ToggleGroup value={view} onChange={setView} options={[{ value: "list", icon: "list", label: "" }, { value: "grid", icon: "grid", label: "" }]} />
      </div>

      {/* Bulk action bar */}
      {selected.size > 0 && (
        <TfCard padding={10} style={{ marginBottom: 12, background: "var(--tf-accent-soft)", borderColor: "var(--tf-accent)" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <span style={{ fontSize: 13, fontWeight: 500 }}>{selected.size} selected</span>
            <div style={{ flex: 1 }} />
            <button onClick={() => setSelected(new Set())} aria-label="Clear the selection" style={{ background: "transparent", border: 0, padding: 6, cursor: "pointer", color: "var(--tf-fg-muted)" }}>
              <Icon name="x" size={16} />
            </button>
          </div>
        </TfCard>
      )}

      {isLoading ? (
        <div style={{ display: "flex", justifyContent: "center", padding: 48 }}><TfSpinner size={28} /></div>
      ) : visible.length === 0 ? (
        <TfCard style={{ marginTop: 12 }}>
          <EmptyState icon="video" title={`No ${filter === "all" ? "" : filter + " "}videos`}
            sub="Try a different filter, or start a new video."
            action={<Btn icon="plus" onClick={() => navigate("/videos/new")}>New video</Btn>} />
        </TfCard>
      ) : view === "list" ? (
        <TfCard padding={0} style={{ overflow: "hidden" }}>
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <thead>
                <tr style={{ background: "var(--tf-bg-2)", borderBottom: "1px solid var(--tf-rule)" }}>
                  <th style={thStyle("48px")}><Checkbox checked={selected.size === visible.length && visible.length > 0} onChange={toggleAll} /></th>
                  <th style={thStyle("auto")}>Video</th>
                  <th style={thStyle("160px")} className="tf-mobile-hide">Status</th>
                  <th style={thStyle("80px")} className="tf-mobile-hide">Format</th>
                  <th style={thStyle("120px")} className="tf-mobile-hide">Created</th>
                  <th style={thStyle("48px")}></th>
                </tr>
              </thead>
              <tbody>
                {visible.map(v => {
                  const sel = selected.has(v.id);
                  return (
                    <tr key={v.id} style={{ borderBottom: "1px solid var(--tf-rule)", background: sel ? "var(--tf-accent-soft)" : "transparent", cursor: "pointer" }}
                      onClick={() => navigate(videoStageRoute(v))}>
                      <td style={tdStyle()} onClick={e => e.stopPropagation()}>
                        <Checkbox checked={sel} onChange={() => toggleSelect(v.id)} />
                      </td>
                      <td style={tdStyle()}>
                        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                          <VideoThumb title={v.scriptText?.split("\n")[0]?.slice(0, 60) ?? "Untitled"} format={v.format}
                            thumbnailUrl={v.selectedThumbnailUrl || v.thumbnailUrls?.[0]} height={56} showTitle={false} />
                          <div style={{ minWidth: 0 }}>
                            <div style={{ fontWeight: 500, color: "var(--tf-fg)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 320 }}>
                              {v.scriptText?.split("\n")[0]?.slice(0, 80) ?? "Untitled video"}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td style={tdStyle()} className="tf-mobile-hide"><VideoStatus v={v} /></td>
                      <td style={tdStyle()} className="tf-mobile-hide"><FormatBadge format={v.format} /></td>
                      <td style={tdStyle()} className="tf-mobile-hide tf-muted">{new Date(v.createdAt).toLocaleDateString()}</td>
                      <td style={tdStyle()} onClick={e => e.stopPropagation()}>
                        <button aria-label="More actions" style={{ background: "transparent", border: 0, padding: 6, cursor: "pointer", color: "var(--tf-fg-muted)", borderRadius: 6 }}>
                          <Icon name="dots" size={16} />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </TfCard>
      ) : (
        <div className="tf-grid-thumbs">
          {visible.map(v => (
            <button key={v.id} onClick={() => navigate(videoStageRoute(v))}
              style={{ background: "transparent", border: 0, padding: 0, cursor: "pointer", textAlign: "left", font: "inherit", color: "inherit" }}>
              <div style={{ position: "relative" }}>
                <VideoThumb title={v.scriptText?.split("\n")[0]?.slice(0, 60) ?? "Untitled"} format={v.format}
                  thumbnailUrl={v.selectedThumbnailUrl || v.thumbnailUrls?.[0]} height={150} />
                <div style={{ position: "absolute", top: 8, left: 8 }}><VideoStatus v={v} /></div>
              </div>
              <div style={{ marginTop: 8, fontSize: 13, fontWeight: 500, lineHeight: 1.35, overflow: "hidden", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" as const }}>
                {v.scriptText?.split("\n")[0]?.slice(0, 80) ?? "Untitled video"}
              </div>
              <div style={{ marginTop: 6, fontSize: 11 }} className="tf-muted">{new Date(v.createdAt).toLocaleDateString()}</div>
            </button>
          ))}
        </div>
      )}
    </PageContainer>
  );
}

const thStyle = (w: string): React.CSSProperties => ({ width: w, textAlign: "left", padding: "10px 12px", fontWeight: 500, fontSize: 11, color: "var(--tf-fg-muted)", textTransform: "uppercase", letterSpacing: "0.05em" });
const tdStyle = (): React.CSSProperties => ({ padding: "12px 12px", verticalAlign: "middle" });
